import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';
import {
	c_PeriodTypeOptions,
	c_DefaultPeriod,
	c_DefaultPeriodType,
	to_date_input_value
} from '../cadence_options';
import '../coldemailer.css';
import '../coldemailer-styles.css';

const c_StatusOptions = [
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'active', label: 'Active' }
];

const c_EmptyForm = {
	name: '',
	description: '',
	email_sender_id: '',
	landing_page_base_url: '',
	unsubscribe_base_url: '',
	status: 'inactive',
	period: c_DefaultPeriod,
	period_type: c_DefaultPeriodType,
	start_date: ''
};

// Today's date is worked out when the modal opens, not when the module loads, so a long lived
// tab does not offer a stale start date
const build_empty_form = () => {
	return Object.assign({}, c_EmptyForm, { start_date: to_date_input_value(new Date()) });
};

function CreateEmailCampaignModal({ show, on_hide, on_created }) {
	const [form_values, set_form_values] = useState(build_empty_form);
	const [email_senders, set_email_senders] = useState([]);
	const [loading, set_loading] = useState(false);
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');

	const load_email_senders = async () => {
		set_loading(true);
		set_error('');

		try {
			const senders = await coldemailer_api.get_email_senders();
			set_email_senders(senders);

			if(senders.length === 1) {
				set_form_values(function(previous) {
					return Object.assign({}, previous, { email_sender_id: senders[0].id });
				});
			}
		} catch (error) {
			console.log('CreateEmailCampaignModal.load_email_senders - ERROR: ', error);
			set_error('Failed to load email senders.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		if(show) {
			set_form_values(build_empty_form());
			set_error('');
			load_email_senders();
		}
	}, [show]);

	const on_field_change = (field_name) => {
		return (event) => {
			const value = event.target.value;
			set_form_values(function(previous) {
				return Object.assign({}, previous, { [field_name]: value });
			});
		};
	};

	const can_save = () => {
		if(!form_values.name.trim()) {
			return false;
		}

		if(!form_values.email_sender_id) {
			return false;
		}

		if(!form_values.landing_page_base_url.trim()) {
			return false;
		}

		// Required at creation, since a campaign without it cannot legally send
		if(!form_values.unsubscribe_base_url.trim()) {
			return false;
		}

		const period = parseInt(form_values.period, 10);
		if(!period || period < 1) {
			return false;
		}

		return true;
	};

	const handle_save = async () => {
		set_saving(true);
		set_error('');

		try {
			// total_emails and end_date follow the campaign's emails, which do not exist yet,
			// so both are left for the campaign editor to fill in once they have been written
			const campaign = await coldemailer_api.create_email_campaign({
				name: form_values.name.trim(),
				description: form_values.description.trim(),
				email_sender_id: form_values.email_sender_id,
				landing_page_base_url: form_values.landing_page_base_url.trim(),
				unsubscribe_base_url: form_values.unsubscribe_base_url.trim(),
				status: form_values.status,
				period: parseInt(form_values.period, 10),
				period_type: form_values.period_type,
				start_date: form_values.start_date ? form_values.start_date : null
			});

			if(on_created) {
				on_created(campaign);
			}

			on_hide();
		} catch (error) {
			console.log('CreateEmailCampaignModal.handle_save - ERROR: ', error);

			const message = error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to create the campaign.';
			set_error(message);
		} finally {
			set_saving(false);
		}
	};

	const handle_hide = () => {
		if(saving) {
			return;
		}

		on_hide();
	};

	const render_no_senders = () => {
		return (
			<Alert variant="danger" className="ce-modal-alert">
				No email senders exist yet. A campaign needs a sender for its compliance footer.
			</Alert>
		);
	};

	const render_form = () => {
		return (
			<Form>
				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Name</Form.Label>
					<Form.Control
						type="text"
						placeholder="Campaign name"
						value={form_values.name}
						onChange={on_field_change('name')}
						disabled={saving}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Description</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						placeholder="What this campaign is for"
						value={form_values.description}
						onChange={on_field_change('description')}
						disabled={saving}
					/>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Sender</Form.Label>
					<Form.Select
						value={form_values.email_sender_id}
						onChange={on_field_change('email_sender_id')}
						disabled={saving || email_senders.length === 0}
					>
						<option value="">Select a sender</option>
						{email_senders.map(function(sender) {
							return (
								<option key={sender.id} value={sender.id}>
									{sender.company_name ? sender.company_name : sender.name}
								</option>
							);
						})}
					</Form.Select>
					<Form.Text className="ce-modal-help">
						Identity shown in the compliance footer of every email.
					</Form.Text>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Landing Page Base URL</Form.Label>
					<Form.Control
						type="text"
						placeholder="https://view.example.com"
						value={form_values.landing_page_base_url}
						onChange={on_field_change('landing_page_base_url')}
						disabled={saving}
					/>
					<Form.Text className="ce-modal-help">
						Each recipient's link becomes this URL plus their unique id.
					</Form.Text>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Unsubscribe Base URL</Form.Label>
					<Form.Control
						type="text"
						placeholder="https://app.example.com"
						value={form_values.unsubscribe_base_url}
						onChange={on_field_change('unsubscribe_base_url')}
						disabled={saving}
					/>
					<Form.Text className="ce-modal-help">
						Host only, with no path: the opt-out path is added automatically. A send is refused without it.
					</Form.Text>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Send Cadence</Form.Label>

					<div className="ce-form-row">
						<Form.Group className="ce-field-period">
							<Form.Control
								type="number"
								min={1}
								step={1}
								value={form_values.period}
								onChange={on_field_change('period')}
								disabled={saving}
							/>
						</Form.Group>

						<Form.Group className="ce-field-period-type">
							<Form.Select
								value={form_values.period_type}
								onChange={on_field_change('period_type')}
								disabled={saving}
							>
								{c_PeriodTypeOptions.map(function(option) {
									return (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									);
								})}
							</Form.Select>
						</Form.Group>
					</div>

					<Form.Text className="ce-modal-help">
						How long to wait before the next email in the sequence.
					</Form.Text>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Start Date</Form.Label>
					<Form.Control
						type="date"
						value={form_values.start_date}
						onChange={on_field_change('start_date')}
						disabled={saving}
					/>
					<Form.Text className="ce-modal-help">
						The end date is worked out once the campaign's emails have been written.
					</Form.Text>
				</Form.Group>

				<Form.Group className="ce-modal-field">
					<Form.Label className="ce-modal-label">Status</Form.Label>
					<Form.Select
						value={form_values.status}
						onChange={on_field_change('status')}
						disabled={saving}
					>
						{c_StatusOptions.map(function(option) {
							return (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							);
						})}
					</Form.Select>
				</Form.Group>
			</Form>
		);
	};

	const render_body = () => {
		if(loading) {
			return (
				<div className="ce-modal-loading">
					<Spinner animation="border" />
				</div>
			);
		}

		return (
			<React.Fragment>
				{email_senders.length === 0 ? render_no_senders() : null}
				{render_form()}
			</React.Fragment>
		);
	};

	return (
		<Modal show={show} onHide={handle_hide} centered dialogClassName="ce-modal-dialog">
			<Modal.Header closeButton>
				<Modal.Title>New Email Campaign</Modal.Title>
			</Modal.Header>

			<Modal.Body className="ce-modal-body">
				{error ? <Alert variant="danger" className="ce-modal-alert">{error}</Alert> : null}
				{render_body()}
			</Modal.Body>

			<Modal.Footer>
				<Button variant="outline-secondary" onClick={handle_hide} disabled={saving}>
					Cancel
				</Button>

				<Button
					className="ce-button-create"
					onClick={handle_save}
					disabled={!can_save() || saving || loading}
				>
					{saving ? 'Creating...' : 'Create Campaign'}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default CreateEmailCampaignModal;
